"use client";

import React, { useEffect, useState } from "react";
import Script from "next/script";

type UpgradChatbotProps = {
  clientKey?: string | null;
  chatBotId?: string | null;
  url?: string | null;
};

type ChatbotEmbedGlobal = {
  config?: { clientKey: string; chatBotId: string };
  onChatBotOpen?: () => void;
};

declare global {
  interface Window {
    ChatbotEmbed?: ChatbotEmbedGlobal;
  }
}

const clean = (v?: string | null) => (v ?? "").trim();

/**
 * Resolve the embed to an absolute http(s) URL, or null when it cannot be. There is
 * no built-in default: an unset URL means the chatbot stays off. Anything not
 * absolute http(s) is refused rather than injected — an empty or relative `src`
 * makes the browser re-fetch the current page and execute the HTML as a script
 * (which can hang the tab), and this value comes from an admin-editable field, so a
 * `javascript:` src would otherwise run arbitrary code.
 */
function resolveEmbed(raw: string): { src: string; origin: string } | null {
  if (!raw) return null;
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
  return { src: parsed.href, origin: parsed.origin };
}

const UpgradChatbot = ({ clientKey, chatBotId, url }: UpgradChatbotProps = {}) => {
  const resolvedClientKey =
    clean(clientKey) || clean(process.env.NEXT_PUBLIC_UPGRAD_CHATBOT_CLIENT_KEY);
  const resolvedBotId = clean(chatBotId) || clean(process.env.NEXT_PUBLIC_UPGRAD_CHATBOT_BOT_ID);
  const rawUrl = clean(url) || clean(process.env.NEXT_PUBLIC_UPGRAD_CHATBOT_URL);

  const embed = resolveEmbed(rawUrl);
  const embedSrc = embed?.src ?? "";
  const embedOrigin = embed?.origin ?? "";

  // Every part must be present and valid, or the chatbot stays off entirely.
  const ready = Boolean(resolvedClientKey && resolvedBotId && embedSrc);

  // The embed reads window.ChatbotEmbed.config while it initialises, so the config
  // must land before the script does. Gating <Script> on this effect guarantees that
  // order; `beforeInteractive` would not, as it only applies in the root layout.
  const [configSet, setConfigSet] = useState(false);

  useEffect(() => {
    if (!ready) return;
    window.ChatbotEmbed = window.ChatbotEmbed || {};
    window.ChatbotEmbed.config = { clientKey: resolvedClientKey, chatBotId: resolvedBotId };
    // Hook the embed calls when the widget opens; no-op until a caller needs it.
    window.ChatbotEmbed.onChatBotOpen = () => {};
    setConfigSet(true);
  }, [ready, resolvedClientKey, resolvedBotId]);

  // Incomplete config means the widget silently never loads; say which part is
  // missing rather than leave whoever configured it guessing.
  useEffect(() => {
    if (ready) return;
    if (rawUrl && !embedSrc) {
      console.warn(
        `[UpgradChatbot] Embed URL ${JSON.stringify(rawUrl)} is not an absolute http(s) URL — chatbot not loaded.`
      );
    } else if (!embedSrc) {
      console.warn("[UpgradChatbot] No embed URL configured — chatbot not loaded.");
    } else {
      console.warn(
        `[UpgradChatbot] Missing ${!resolvedClientKey ? "client key" : "bot id"} — chatbot not loaded.`
      );
    }
  }, [ready, rawUrl, embedSrc, resolvedClientKey]);

  if (!ready) return null;

  return (
    <>
      <link rel="preconnect" href={embedOrigin} />
      {configSet && <Script id="upgrad-chatbot-embed" src={embedSrc} strategy="afterInteractive" />}
    </>
  );
};

export default UpgradChatbot;
